import { ProvidersList as ProvidersListUI } from '@openx/components-provider/lib/components/ProvidersList/ProvidersList.tsx';
import type { ProvidersListFilters } from '@openx/components-provider/lib/components/types.ts';
import { useProviderContext } from '@openx/components-provider/lib/utils/context/ProviderContext.tsx';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing.ts';
import type { CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { useHandleListCriteriaOnCompleted } from '../../../hooks/useHandleListCriteriaOnCompleted.ts';
import { useListCriteria } from '../../../utils/useListCriteria.ts';
import { defaultCriteria } from '../constants.ts';
import { adjustCriteriaFilters } from './utils/adjustCriteriaFilters.ts';

export const ProvidersList = () => {
  const { criteria, setCriteria } = useListCriteria('providers', defaultCriteria);
  const { useProvidersListFetch } = useProviderContext();
  const queryFilters = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);

  const queryCrit = useMemo(
    () => serverSideCriteriaProcessing({ ...criteria, filters: queryFilters }, [defaultCriteria.sort]),
    [criteria, queryFilters]
  );

  const handleCompleted = useHandleListCriteriaOnCompleted<ProvidersListFilters>(setCriteria);

  const { error, loading, providers } = useProvidersListFetch({
    onCompleted: handleCompleted,
    variables: queryCrit,
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, ProvidersListFilters>) => {
      setCriteria(prevCriteria => {
        const newCriteriaProcessed = applyCriteriaChange(prevCriteria, newCriteria);
        if (!isEqual(newCriteriaProcessed, prevCriteria)) {
          return newCriteriaProcessed;
        }
        return prevCriteria;
      });
    },
    [setCriteria]
  );

  if (error) throw new Error('Providers Error');

  return (
    <ProvidersListUI
      criteria={criteria}
      handleCriteriaChange={handleCriteriaChange}
      loading={loading}
      providers={providers}
    />
  );
};
